import { Brand } from "@/core/Brand";
import { Store } from "@/core/Store";
import { useRef, useState } from "react";

import { EnvironmentAPI } from "@/api/EnvironmentAPI";
import { Button } from "@/components";
import styles from "@/styles/containers/configure.module.scss";
import Image from "next/image";

import classNames from "classnames/bind";
import { ConfigurationFieldInput } from "./ConfigurationFieldInput";
import { ConfigurationFieldLabel } from "./ConfigurationFieldLabel";

import { NetLogicAPI } from "@/api/NetLogicAPI";
import { Alert } from "@/components/Alert";
import { useAppConfiguration } from "@/hooks/useAppConfiguration";
import { useEcleticaTableConfiguration } from "@/hooks/useEcleticaTableConfiguration";
import { useNetLogicTableConfiguration } from "@/hooks/useNetLogicTableConfiguration";
import Bugsnag from "@bugsnag/js";
import PasswordConfirm from "../Auth/PasswordConfirm";
import Logs from "./Logs";

import EcleticaLogo from "/public/ecletica.png";
import NetLogicLogo from "/public/netlogic.png";
import ColibriLogo from "/public/colibri.png";
import SaiposLogo from "/public/saipos.svg";
import ColibriConfiguration from "./ColibriConfiguration";
import { QRCodeConfigurationDrawer } from "../QRCode/QRCodeConfigurationDrawer";
import SaiposConfiguration from "./SaiposConfiguration";

const css = classNames.bind(styles);

const pdvs = {
  ECLETICA: "ECLETICA",
  NETLOGIC: "NETLOGIC",
  COLIBRI: "COLIBRI",
  SAIPOS: "SAIPOS",
};

export default function Configure({
  onSuccess,
  onBack,
  onlyDailyConfiguration = false,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [fieldToEdit, setFieldToEdit] = useState(null);
  const [selectedPDV, setSelectedPDV] = useState(null);
  const [readyToConfigure, setReadyToConfigure] = useState(false);

  const [openLogs, setOpenLogs] = useState(false);
  const [openQRCode, setOpenQRCode] = useState(false);

  const [canEditNetlogic, setCanEditNetlogic] = useState(
    !onlyDailyConfiguration,
  );

  const [authenticated, setAuthenticated] = useState(false);

  const [appConfigurations, updateAppConfigurations, persistAppConfigurations] =
    useAppConfiguration();

  const [configurations, updateConfigurations, persistConfigurations, isValid] =
    useNetLogicTableConfiguration();
  const [
    configurationsEcletica,
    updateConfigurationsEcletica,
    persistConfigurationsEcletica,
    isValidEcletica,
  ] = useEcleticaTableConfiguration();

  const intervalRef = useRef(null);

  const startPressedCounter = () => {
    if (intervalRef.current) return;

    intervalRef.current = setInterval(() => {
      stopPressedCounter();
      setCanEditNetlogic(true);
    }, 1000);
  };

  const stopPressedCounter = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const onOpenNetlogic = async () => {
    setLoading(true);

    const isValidNetlogicConfig = await checkNetLogicConfiguration();

    if (isValidNetlogicConfig) {
      persistConfigurations();
      persistAppConfigurations();

      try {
        await loadEnvironment(appConfigurations.unitCode);
      } catch (err) {
        console.error(err);
      }
    }

    setLoading(false);
  };

  const onOpenEcletica = async () => {
    setLoading(true);

    if (isValidEcletica) {
      persistConfigurationsEcletica();
      persistAppConfigurations();

      try {
        await loadEnvironment(appConfigurations.unitCode);
      } catch (err) {
        console.error(err);
      }
    }
  };

  const checkNetLogicConfiguration = async () => {
    const netLogicClient = new NetLogicAPI({
      baseUrl: configurations.apiUrl,
      apiToken: configurations.apiToken,
    });

    const catalogResponse = await netLogicClient.getCatalog();

    if (catalogResponse.getErrors([]).length > 0) {
      Bugsnag.notify(
        new Error(
          `Falha no carregamento do cardápio - ${
            catalogResponse.getErrors()[0]
          }`,
        ),
      );
      setError(true);
      setErrorMessage(
        `Falha no carregamento do cardápio - ${catalogResponse.getErrors()[0]}`,
      );
      return false;
    }

    return true;
  };

  const loadEnvironment = async (code) => {
    try {
      setLoading(true);

      const environmentResponse =
        await EnvironmentAPI.getEnvironmentByCode(code);

      const data = environmentResponse.getData({});
      const brandAlias = data.brandAlias;
      const storeAlias = data.unitAlias;

      if (environmentResponse.getErrors([]).length === 0) {
        await Brand.install(brandAlias);
        await Store.install(storeAlias);

        Brand.alias = brandAlias;
        Store.alias = storeAlias;

        onSuccess();
      } else {
        setError(true);
        setErrorMessage(`${environmentResponse.getErrors([])?.[0]}`);
      }
    } catch (err) {
      setError(true);
      setErrorMessage(`${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!authenticated && (
        <PasswordConfirm
          onConfirm={() => setAuthenticated(true)}
          onBack={onBack}
        />
      )}

      {authenticated && !readyToConfigure && (
        <div className={styles.configureContainer}>
          <div className={css({ welcome: true, show: !fieldToEdit })}>
            <div
              className={styles.logo}
              onMouseDown={startPressedCounter}
              onTouchStart={startPressedCounter}
              onTouchEnd={stopPressedCounter}
              onMouseUp={stopPressedCounter}
              onMouseLeave={stopPressedCounter}
            >
              <Image
                src="https://olga.tech/favicon-olga.png"
                alt="Olga"
                width={95}
                height={95}
              />
            </div>

            <p className={styles.title}>Boas vindas</p>

            <p className={styles.version}>
              App {process.env.NEXT_PUBLIC_VERSION}
            </p>

            <Button
              design="primary"
              className={styles.logButton}
              onClick={() => setOpenLogs(true)}
            >
              Visualizar logs
            </Button>

            <div className={styles.pdvs}>
              <div
                className={css({
                  pdv: true,
                  selected: selectedPDV === pdvs.ECLETICA,
                })}
                onClick={() => {
                  updateAppConfigurations({
                    tableService: "ECLETICA",
                  });
                  setSelectedPDV(pdvs.ECLETICA);
                }}
              >
                <div className={styles.pdvLogo}>
                  <Image src={EcleticaLogo} alt={pdvs.ECLETICA} fill />
                </div>
              </div>

              <div
                className={css({
                  pdv: true,
                  selected: selectedPDV === pdvs.COLIBRI,
                })}
                onClick={() => {
                  updateAppConfigurations({
                    tableService: "COLIBRI",
                  });
                  setSelectedPDV(pdvs.COLIBRI);
                }}
              >
                <div className={styles.pdvLogo}>
                  <Image src={ColibriLogo} alt={pdvs.COLIBRI} fill />
                </div>
              </div>

              <div
                className={css({
                  pdv: true,
                  selected: selectedPDV === pdvs.NETLOGIC,
                })}
                onClick={() => {
                  updateAppConfigurations({
                    tableService: "NETLOGIC",
                  });
                  setSelectedPDV(pdvs.NETLOGIC);
                }}
              >
                <div className={styles.pdvLogo}>
                  <Image src={NetLogicLogo} alt={pdvs.NETLOGIC} fill />
                </div>
              </div>

              <div
                className={css({
                  pdv: true,
                  selected: selectedPDV === pdvs.SAIPOS,
                })}
                onClick={() => {
                  updateAppConfigurations({
                    tableService: "SAIPOS",
                  });
                  setSelectedPDV(pdvs.SAIPOS);
                }}
              >
                <div className={styles.pdvLogo}>
                  <Image src={SaiposLogo} alt={pdvs.SAIPOS} fill />
                </div>
              </div>
            </div>

            <div className={styles.footer}>
              <div></div>
              <Button
                onClick={() => setReadyToConfigure(true)}
                disabled={!selectedPDV}
              >
                Avançar
              </Button>
            </div>
          </div>
        </div>
      )}

      {authenticated && readyToConfigure && (
        <div className={styles.configureContainer}>
          {selectedPDV === pdvs.COLIBRI && (
            <ColibriConfiguration
              onlyDailyConfiguration={onlyDailyConfiguration}
              onBack={() => {
                setReadyToConfigure(false);
              }}
              onConfirm={onSuccess}
            />
          )}

          {selectedPDV === pdvs.SAIPOS && (
            <SaiposConfiguration
              onlyDailyConfiguration={onlyDailyConfiguration}
              onBack={() => {
                setReadyToConfigure(false);
              }}
              onConfirm={onSuccess}
              onOpenQRCode={() => {
                setOpenQRCode(true);
              }}
            />
          )}

          {[pdvs.ECLETICA, pdvs.NETLOGIC].includes(selectedPDV) && (
            <div className={css({ welcome: true, show: !fieldToEdit })}>
              <div
                className={styles.logo}
                onMouseDown={startPressedCounter}
                onTouchStart={startPressedCounter}
                onTouchEnd={stopPressedCounter}
                onMouseUp={stopPressedCounter}
                onMouseLeave={stopPressedCounter}
              >
                <Image
                  src="https://olga.tech/favicon-olga.png"
                  alt="Olga"
                  width={95}
                  height={95}
                />
              </div>
              <p className={styles.title}>Boas vindas</p>
              <p className={styles.version}>
                App {process.env.NEXT_PUBLIC_VERSION}
              </p>

              <Button
                design="primary"
                className={styles.logButton}
                onClick={() => setOpenLogs(true)}
              >
                Visualizar logs
              </Button>

              {selectedPDV === pdvs.ECLETICA && (
                <>
                  <ConfigurationFieldLabel
                    label="Código da unidade"
                    value={appConfigurations.unitCode}
                    onClick={() => setFieldToEdit("UNIT")}
                  />

                  <ConfigurationFieldLabel
                    label="URL da API"
                    value={configurationsEcletica.apiUrl}
                    onClick={() => setFieldToEdit("API_URL")}
                  />

                  <ConfigurationFieldLabel
                    label="Token da API"
                    value={configurationsEcletica.apiToken}
                    onClick={() => setFieldToEdit("API_TOKEN")}
                  />

                  <ConfigurationFieldLabel
                    label="Código da mesa"
                    value={configurationsEcletica.externalId}
                    onClick={() => setFieldToEdit("TABLE")}
                  />

                  <ConfigurationFieldLabel
                    label="SKU do produto (Chamar garçom fechamento)"
                    value={configurationsEcletica.callWaiterClosingSku}
                    onClick={() =>
                      setFieldToEdit("ECLETICA_CALL_WAITER_CLOSING_SKU")
                    }
                  />

                  <ConfigurationFieldLabel
                    label="Exibir proteção infantil antes de enviar o pedido"
                    value={
                      !!appConfigurations.allowChallenge === true
                        ? "Sim"
                        : "Não"
                    }
                    onClick={() => {
                      updateAppConfigurations({
                        allowChallenge: !appConfigurations.allowChallenge,
                      });
                    }}
                  />

                  {/* <ConfigurationFieldLabel
                      label="Rede"
                      value={configurationsEcletica.network}
                      onClick={() => setFieldToEdit("NETWORK")}
                    />

                    <ConfigurationFieldLabel
                      label="Loja"
                      value={configurationsEcletica.store}
                      onClick={() => setFieldToEdit("STORE")}
                    /> */}
                </>
              )}
              {selectedPDV === pdvs.NETLOGIC && (
                <>
                  <Button
                    design="primary"
                    className={styles.logButton}
                    onClick={() => setOpenQRCode(true)}
                  >
                    Ler QR Code
                  </Button>

                  {canEditNetlogic && (
                    <>
                      <ConfigurationFieldLabel
                        label="Código da unidade"
                        value={appConfigurations.unitCode}
                        onClick={() => setFieldToEdit("UNIT")}
                      />

                      <ConfigurationFieldLabel
                        label="URL da API"
                        value={configurations.apiUrl}
                        onClick={() => setFieldToEdit("API_URL")}
                      />

                      {/* <ConfigurationFieldLabel
                          label="Token da API"
                          value={configurations.apiToken}
                          onClick={() => setFieldToEdit("API_TOKEN")}
                        /> */}

                      <ConfigurationFieldLabel
                        label="Tipo do pedido (order.type)"
                        value={configurations.orderType}
                        onClick={() => setFieldToEdit("ORDER_TYPE")}
                      />

                      <ConfigurationFieldLabel
                        label="RVC (revenueCenter)"
                        value={configurations.revenueCenter}
                        onClick={() => setFieldToEdit("REVENUE_CENTER")}
                      />

                      <ConfigurationFieldLabel
                        label="Tender Media (Manter a mesa aberta)"
                        value={configurations.tenderMediaIncrement}
                        onClick={() => setFieldToEdit("TENDER_MEDIA_INCREMENT")}
                      />

                      <ConfigurationFieldLabel
                        label="Tender Media (Impressão do pré-fechamento)"
                        value={configurations.tenderMediaClosing}
                        onClick={() => setFieldToEdit("TENDER_MEDIA_CLOSING")}
                      />

                      <ConfigurationFieldLabel
                        label="SKU do produto (Chamar garçom)"
                        value={configurations.callWaiterSku}
                        onClick={() => setFieldToEdit("CALL_WAITER_SKU")}
                      />

                      <ConfigurationFieldLabel
                        label="SKU do produto (Chamar garçom fechamento)"
                        value={configurations.callWaiterClosingSku}
                        onClick={() =>
                          setFieldToEdit("CALL_WAITER_CLOSING_SKU")
                        }
                      />
                    </>
                  )}

                  <ConfigurationFieldLabel
                    label="Código do garçom"
                    value={configurations.orderEmployee}
                    onClick={() => setFieldToEdit("EMPLOYEE")}
                  />

                  <ConfigurationFieldLabel
                    label="Código da mesa"
                    value={configurations.externalId}
                    onClick={() => setFieldToEdit("TABLE")}
                  />

                  <ConfigurationFieldLabel
                    label="Exibir proteção infantil antes de enviar o pedido"
                    value={
                      !!appConfigurations.allowChallenge === true
                        ? "Sim"
                        : "Não"
                    }
                    onClick={() => {
                      updateAppConfigurations({
                        allowChallenge: !appConfigurations.allowChallenge,
                      });
                    }}
                  />
                </>
              )}
              {selectedPDV === pdvs.NETLOGIC && (
                <div className={styles.footer}>
                  <Button
                    design="secondary"
                    onClick={() => setReadyToConfigure(false)}
                  >
                    Voltar
                  </Button>
                  <Button
                    onClick={onOpenNetlogic}
                    loading={loading}
                    disabled={
                      loading || !(appConfigurations?.unitCode && isValid)
                    }
                  >
                    Abrir
                  </Button>
                </div>
              )}
              {selectedPDV === pdvs.ECLETICA && (
                <div className={styles.footer}>
                  <Button
                    design="secondary"
                    onClick={() => setReadyToConfigure(false)}
                  >
                    Voltar
                  </Button>
                  <Button
                    onClick={onOpenEcletica}
                    loading={loading}
                    disabled={
                      loading ||
                      !(appConfigurations?.unitCode && isValidEcletica)
                    }
                  >
                    Abrir
                  </Button>
                </div>
              )}
            </div>
          )}

          {fieldToEdit === "UNIT" && (
            <ConfigurationFieldInput
              label="Insira o código da unidade"
              value={appConfigurations.unitCode}
              onValidate={(value) => !!value}
              onCancel={() => {
                setFieldToEdit(null);
              }}
              onConfirm={(value) => {
                setFieldToEdit(null);
                updateAppConfigurations({ unitCode: value });
              }}
            />
          )}

          {fieldToEdit === "API_URL" && (
            <ConfigurationFieldInput
              label="Insira a URL da API"
              value={configurations.apiUrl || configurationsEcletica.apiUrl}
              onValidate={(value) => !!value}
              type="text"
              onCancel={() => {
                setFieldToEdit(null);
              }}
              onConfirm={(value) => {
                setFieldToEdit(null);

                if (selectedPDV === pdvs.ECLETICA)
                  updateConfigurationsEcletica({ apiUrl: value });
                if (selectedPDV === pdvs.NETLOGIC)
                  updateConfigurations({ apiUrl: value });
              }}
            />
          )}

          {fieldToEdit === "API_TOKEN" && (
            <ConfigurationFieldInput
              label="Insira o token da API"
              value={configurationsEcletica.apiToken}
              onValidate={(value) => !!value}
              type="text"
              onCancel={() => {
                setFieldToEdit(null);
              }}
              onConfirm={(value) => {
                setFieldToEdit(null);

                if (selectedPDV === pdvs.ECLETICA)
                  updateConfigurationsEcletica({ apiToken: value });
              }}
            />
          )}

          {fieldToEdit === "TABLE" && (
            <ConfigurationFieldInput
              label="Insira o código da mesa"
              value={
                selectedPDV === pdvs.ECLETICA
                  ? configurationsEcletica.externalId
                  : configurations.externalId
              }
              onValidate={(value) => !!value}
              onCancel={() => {
                setFieldToEdit(null);
              }}
              onConfirm={(value) => {
                setFieldToEdit(null);

                if (selectedPDV === pdvs.ECLETICA)
                  updateConfigurationsEcletica({ externalId: value });
                if (selectedPDV === pdvs.NETLOGIC)
                  updateConfigurations({ externalId: value });
              }}
            />
          )}

          {fieldToEdit === "EMPLOYEE" && (
            <ConfigurationFieldInput
              label="Insira o código do garçom"
              maxLength="8"
              value={configurations.orderEmployee}
              onValidate={(value) => !!value}
              onCancel={() => setFieldToEdit(null)}
              onConfirm={(value) => {
                setFieldToEdit(null);
                updateConfigurations({ orderEmployee: value });
              }}
            />
          )}

          {fieldToEdit === "ORDER_TYPE" && (
            <ConfigurationFieldInput
              label="Insira o código do tipo do pedido"
              maxLength="8"
              value={configurations.orderType}
              onValidate={(value) => !!value}
              onCancel={() => setFieldToEdit(null)}
              onConfirm={(value) => {
                setFieldToEdit(null);
                updateConfigurations({ orderType: value });
              }}
            />
          )}

          {fieldToEdit === "REVENUE_CENTER" && (
            <ConfigurationFieldInput
              label="RVC"
              maxLength="15"
              value={configurations.revenueCenter}
              onValidate={(value) => !!value}
              onCancel={() => setFieldToEdit(null)}
              onConfirm={(value) => {
                setFieldToEdit(null);
                updateConfigurations({ revenueCenter: value });
              }}
            />
          )}

          {fieldToEdit === "TENDER_MEDIA_INCREMENT" && (
            <ConfigurationFieldInput
              label="Insira o código do tender media"
              maxLength="8"
              value={configurations.tenderMediaIncrement}
              onValidate={(value) => !!value}
              onCancel={() => setFieldToEdit(null)}
              onConfirm={(value) => {
                setFieldToEdit(null);
                updateConfigurations({
                  tenderMediaIncrement: value,
                });
              }}
            />
          )}

          {fieldToEdit === "TENDER_MEDIA_CLOSING" && (
            <ConfigurationFieldInput
              label="Insira o código do tender media"
              maxLength="8"
              value={configurations.tenderMediaClosing}
              onValidate={(value) => !!value}
              onCancel={() => setFieldToEdit(null)}
              onConfirm={(value) => {
                setFieldToEdit(null);
                updateConfigurations({
                  tenderMediaClosing: value,
                });
              }}
            />
          )}

          {fieldToEdit === "CALL_WAITER_SKU" && (
            <ConfigurationFieldInput
              label="Insira o SKU do produto que será enviado ao chamar o garçom"
              maxLength="8"
              value={configurations.callWaiterSku}
              onValidate={(value) => !!value}
              onCancel={() => setFieldToEdit(null)}
              onConfirm={(value) => {
                setFieldToEdit(null);
                updateConfigurations({
                  callWaiterSku: value,
                });
              }}
            />
          )}

          {fieldToEdit === "CALL_WAITER_CLOSING_SKU" && (
            <ConfigurationFieldInput
              label="Insira o SKU do produto que será enviado ao chamar o garçom para fechamento (opcional)"
              maxLength="8"
              value={configurations.callWaiterClosingSku}
              onValidate={(value) => true}
              onCancel={() => setFieldToEdit(null)}
              onConfirm={(value) => {
                setFieldToEdit(null);
                updateConfigurations({
                  callWaiterClosingSku: value,
                });
              }}
            />
          )}

          {fieldToEdit === "ECLETICA_CALL_WAITER_CLOSING_SKU" && (
            <ConfigurationFieldInput
              label="Insira o SKU do produto que será enviado ao chamar o garçom"
              maxLength="8"
              value={configurationsEcletica.callWaiterClosingSku}
              onValidate={(value) => true}
              onCancel={() => setFieldToEdit(null)}
              onConfirm={(value) => {
                setFieldToEdit(null);
                updateConfigurationsEcletica({
                  callWaiterClosingSku: value,
                });
              }}
            />
          )}
        </div>
      )}

      {openLogs && <Logs onClose={() => setOpenLogs(false)} />}

      {openQRCode && (
        <QRCodeConfigurationDrawer
          type={selectedPDV}
          onUpdate={() => setOpenQRCode(false)}
          onClose={() => setOpenQRCode(false)}
        />
      )}

      <Alert
        title={errorMessage}
        subtitle="Não foi possível carregar os dados da unidade"
        open={!!error}
        onClose={() => setError(null)}
      />
    </>
  );
}
